import React, { Suspense, useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import * as apiClient from '../api-client';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Loader from '../components/LoaderComponent';
import LinkButton from '../components/Common/LinkButton';
import HotelCardSkeleton from '../components/Skeletons/HotelCardsSkeleton';
import HotelSliderSkeleton from '../components/Skeletons/HotelSliderSkeleton';
import BlogSliderHome from '../components/Blogs/BlogSliderHome';
import useOnScreen from '../hooks/useOnScreen';
import TrustBox from '../components/TrustBox';
import ReadMoreLines from '../components/ReadMoreLines';
import LogoLoop from '../components/LogoLoop';

// Lazy load the components
const AllInclusiveSlider = React.lazy(() => import('../components/Sliders/AllInclusiveSlider'));
const LazyWhyChoose = React.lazy(() => import('../components/Home/WhyChoose'));
const AllInclusiveHotelsList = React.lazy(() => import('../components/HotelCard/AllInclusiveHotelsList'));
const BeachHotelsList = React.lazy(() => import('../components/HotelCard/BeachHotelsList'));
const BeachSlider = React.lazy(() => import('../components/Sliders/BeachSlider'));
const FamilyHotelsList = React.lazy(() => import('../components/HotelCard/FamilyHotelsList'));
const FamilySlider = React.lazy(() => import('../components/Sliders/FamilySlider'));
const LuxuryHotelList = React.lazy(() => import('../components/HotelCard/LuxuryHotelsList'));
const LuxurySlider = React.lazy(() => import('../components/Sliders/LuxurySlider'));
const HoneyMoonHotelsList = React.lazy(() => import('../components/HotelCard/HoneymoonHotelsList'));
const HoneymoonSlider = React.lazy(() => import('../components/Sliders/HoneymoonSlider'));

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [startFetching, setStartFetching] = useState(false);

  // Delay the fetchHotels call by 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setStartFetching(true);
    }, 7000);

    return () => clearTimeout(timer);
  }, []);

  const { data: hotels, error, isFetching } = useQuery('fetchHotels', apiClient.fetchHotels, {
    enabled: startFetching
  });

  if (error) return <div>Error loading hotels.</div>;

  // Create refs for each section
  const sectionsRefs = {
    latestDestinations: useRef<HTMLDivElement>(null),
    vacationTypes: useRef<HTMLDivElement>(null),
    allInclusive: useRef<HTMLDivElement>(null),
    family: useRef<HTMLDivElement>(null),
    couple: useRef<HTMLDivElement>(null),
    luxury: useRef<HTMLDivElement>(null),
    honeymoon: useRef<HTMLDivElement>(null),
    beach: useRef<HTMLDivElement>(null),
    lastMinute: useRef<HTMLDivElement>(null),
    budgetDeals: useRef<HTMLDivElement>(null),
    blogSlider: useRef<HTMLDivElement>(null),
  };

  // Use the custom hook for each section
  const allInclusiveVisibility = useOnScreen(sectionsRefs.allInclusive);
  const familyVisibility = useOnScreen(sectionsRefs.family);
  const luxuryVisibility = useOnScreen(sectionsRefs.luxury);
  const honeymoonVisibility = useOnScreen(sectionsRefs.honeymoon);
  const beachVisibility = useOnScreen(sectionsRefs.beach);
  const blogSliderVisibility = useOnScreen(sectionsRefs.blogSlider);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Book Cheap Holidays & All-Inclusive Getaways | Halo Holidays</title>
          <meta name="description" content="Discover affordable holidays and all-inclusive escapes with Halo Holidays. Expert travel advice, handpicked destinations, and unbeatable deals for your next adventure." />
        </Helmet>
      </HelmetProvider>
      <main className="mx-1 -mt-2 md:mt-3 lg:md-0 md:mx-0 lg:mx-0 ">

        <section className="max-h-640 md:min-h-0 lg:min-h-0 my-8">
          <hr className="mb-10" />
          <h2 className="text-3xl font-semibold items-center justify-center flex mt-10">More reasons to choose us</h2>
          <Suspense fallback={<Loader />}>
            <LazyWhyChoose />
          </Suspense>
          <hr className="mb-10 mt-10" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 my-12 p-6">
          <div className="flex flex-col items-center justify-center text-center space-y-6 -mt-10">
            <h2 className="text-3xl font-extrabold text-gray-800">British Travel Award Winner</h2>
            <img
              src="/general/BTA.png"
              alt="British Travel Awards"
              className="max-w-full md:h-[17rem] h-[12rem]"
              loading="lazy"
            />
          </div>

          <div>
            <h2 className="text-xl font-extrabold text-gray-800 text-center mb-8 -mt-4">As Seen In</h2>
            <Suspense fallback={<Loader />}>
              <LogoLoop />
            </Suspense>
            <div className="text-center flex flex-col items-center justify-center mt-10">
              <p className="text-gray-700 text-sm leading-relaxed">
                We are thrilled to announce that we, <span className='text-[#d7006a]'>Halo Holidays</span>, has been honored with <strong>Gold</strong> at the <strong>British Travel Awards for South East Asia!</strong> This prestigious recognition is a testament to our commitment to excellence and the trust you, our valued customers, have placed in us.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                A heartfelt thank you to everyone who supported us on this incredible journey. Your loyalty and encouragement inspire us to continue delivering unforgettable travel experiences.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                Here’s to many more adventures together! ✈️🌏
              </p>
            </div>
          </div>
        </section>
        <hr className="mb-10 mt-10" />

        {isFetching && <HotelSliderSkeleton />}

        <section ref={sectionsRefs.allInclusive} className="mx-4 my-4 sm:my-6 md:mx-0 md:my-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold hidden md:block lg:block lgx2:block">Stress-Free All-Inclusive Holidays</h2>
              <h2 className="text-2xl font-semibold md:hidden lg:hidden lgx2:hidden">All-Inclusive Holidays</h2>
            </div>
          </div>
          <div className="flex flex-col">
            <ReadMoreLines mobileMaxRows={2} desktopMaxRows={2}>
              <p className="text-black md:mb-1 md:mt-2">
                We are specialized in providing exceptional all inclusive holidays tailored to meet the diverse needs of families, couples, and solo travelers. With a focus on premier destinations such as the Maldives, Japan, South Korea, Sri Lanka, the Caribbean, Thailand, Dubai, and Turkey, we ensure a seamless travel experience that encompasses accommodation, meals, and activities. Our all inclusive packages are designed to offer relaxation and adventure, allowing guests to fully immerse themselves in their chosen destination without the stress of additional costs. For more information on our all inclusive resorts, please click below.
              </p>
            </ReadMoreLines>
          </div>
          {allInclusiveVisibility.hasBeenVisible && (
            <>
              <div className="hidden md:block lg:block lgx2:block mt-2">
                <Suspense fallback={<HotelCardSkeleton />}>
                  <AllInclusiveHotelsList hotels={hotels ?? []} />
                </Suspense>
              </div>
              <div className="container md:hidden lg:hidden">
                <Suspense fallback={<HotelSliderSkeleton />}>
                  <AllInclusiveSlider hotels={hotels ?? []} />
                </Suspense>
              </div>
              <div className='hidden md:block lg:block lgx2:block'>
                <LinkButton to='/all-inclusive-holidays' btnDescription='View All Deals' />
              </div>
            </>
          )}
        </section>
        <hr className="mb-10 mt-10" />

        <section ref={sectionsRefs.family} className="mx-4 my-4 sm:my-6 md:mx-0 md:my-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold hidden md:block lg:block lgx2:block">Unforgettable Family Holidays</h2>
              <h2 className="text-2xl font-semibold md:hidden lg:hidden lgx2:hidden">Family Holidays</h2>
            </div>
          </div>
          <div className="flex flex-col">
            <ReadMoreLines mobileMaxRows={2} desktopMaxRows={2}>
              <p className="text-black md:mb-2 md:mt-2">
                Our commitment is to offer  unforgettable family holidays, providing exceptional travel experiences tailored for families seeking adventure and relaxation. With a diverse range of destinations including the Maldives, Japan, South Korea, Sri Lanka, the Caribbean, Thailand, Dubai, and Turkey, families can explore vibrant cultures, stunning landscapes, and luxurious accommodations. Each holiday package is designed to ensure that every family member enjoys their time together, creating lasting memories. Discover best family resorts that cater to families of all sizes and preferences. Click below to explore our exclusive offers and start planning your perfect family holiday today.
              </p>
            </ReadMoreLines>
          </div>
          {familyVisibility.hasBeenVisible && (
            <>
              <div className="hidden md:block lg:block lgx2:block mt-2">
                <Suspense fallback={<HotelCardSkeleton />}>
                  <FamilyHotelsList hotels={hotels ?? []} />
                </Suspense>
              </div>
              <div className="container md:hidden lg:hidden">
                <Suspense fallback={<HotelSliderSkeleton />}>
                  <FamilySlider hotels={hotels ?? []} />
                </Suspense>
              </div>
              <div className='hidden md:block lg:block lgx2:block'>
                <LinkButton to='/family-holidays' btnDescription='View All Deals' />
              </div>
            </>
          )}
        </section>
        <hr className="mb-10 mt-10" />

        <section ref={sectionsRefs.blogSlider} className={`p-4 md:p-0 mx-4 my-4 sm:my-6 md:mx-0 md:my-8 md:block lg:block lgx2:block`}>
          <h2 className="text-2xl font-semibold mb-2">Explore, Dream, Discover: Travel Stories & Guides</h2>
          {blogSliderVisibility.hasBeenVisible && (
            <div>
              <BlogSliderHome />
            </div>
          )}
        </section>

        <section className="relative w-full md:mx-4 my-4 sm:my-6 md:mx-0 md:my-8">
          <TrustBox />
        </section>
        <hr className="mb-10 mt-10" />

        <section ref={sectionsRefs.luxury} className="mx-4 my-4 sm:my-6 md:mx-0 md:my-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold hidden md:block lg:block lgx2:block">Unforgettable Luxury Holidays</h2>
              <h2 className="text-2xl font-semibold md:hidden lg:hidden lgx2:hidden">Luxury Holidays</h2>
            </div>
          </div>
          <div className="flex flex-col">
            <ReadMoreLines mobileMaxRows={2} desktopMaxRows={2}>
              <p className="text-black md:mb-2 md:mt-2">
                Halo Holidays specializes in providing exceptional luxury holidays tailored for discerning travelers seeking unforgettable experiences. Our curated selection of destinations includes the pristine beaches of the Maldives, the cultural richness of Japan and South Korea, the vibrant landscapes of Sri Lanka, the idyllic Caribbean, the exotic allure of Thailand, the opulence of Dubai, and the historical charm of Turkey. Each holiday is designed to offer unparalleled comfort and indulgence, ensuring that every moment is a celebration of luxury. For those ready to embark on a journey of a lifetime, click below to explore our exquisite luxury resorts.
              </p>
            </ReadMoreLines>
          </div>
          {luxuryVisibility.hasBeenVisible && (
            <>
              <div className="hidden md:block lg:block lgx2:block mt-2">
                <Suspense fallback={<HotelCardSkeleton />}>
                  <LuxuryHotelList hotels={hotels ?? []} />
                </Suspense>
              </div>
              <div className="container md:hidden lg:hidden">
                <Suspense fallback={<HotelSliderSkeleton />}>
                  <LuxurySlider hotels={hotels ?? []} />
                </Suspense>
              </div>
              <div className='hidden md:block lg:block lgx2:block'>
                <LinkButton to='/luxury-holidays' btnDescription='View All Deals' />
              </div>
            </>
          )}
        </section>
        <hr className="mb-10 mt-10" />

        <section className="mx-4 my-6 sm:my-6 md:mx-0 md:my-8">
          <div className='hidden md:block'>
            <img src='/general/Combine.png' alt='Combine & Save' loading="lazy" />
          </div>
          <div className='md:hidden'>
            <img src='/general/Combine-Mobile.png' alt='Combine & Save' className='rounded-md' loading="lazy" />
          </div>
        </section>
        <hr className="mb-10 mt-10" />

        <section ref={sectionsRefs.honeymoon} className="mx-4 my-4 sm:my-6 md:mx-0 md:my-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold hidden md:block lg:block lgx2:block">Dream Honeymoon Holidays</h2>
              <h2 className="text-2xl font-semibold md:hidden lg:hidden lgx2:hidden">Honeymoon Holidays</h2>
            </div>
          </div>
          <div className="flex flex-col">
            <ReadMoreLines mobileMaxRows={2} desktopMaxRows={2}>
              <p className="text-black md:mb-2 md:mt-2">
                We are dedicated to providing remarkable honeymoon experiences, offering romantic getaways that cater to couples seeking unforgettable holidays. Explore idyllic destinations such as the Maldives, Japan, South Korea, Sri Lanka, Bali, the Caribbean, Thailand, Dubai, and Turkey, each providing a perfect backdrop for love and relaxation. Indulge in opulent accommodations, exquisite dining, and personalized services designed to create lasting memories. Whether you desire serene beaches or vibrant cultures, our curated selection ensures an enchanting escape for every couple. Click below to discover our exclusive honeymoon resorts and start planning your dream getaway today.
              </p>
            </ReadMoreLines>
          </div>
          {honeymoonVisibility.hasBeenVisible && (
            <>
              <div className="hidden md:block lg:block lgx2:block mt-2">
                <Suspense fallback={<HotelCardSkeleton />}>
                  <HoneyMoonHotelsList hotels={hotels ?? []} />
                </Suspense>
              </div>
              <div className="container md:hidden lg:hidden">
                <Suspense fallback={<HotelSliderSkeleton />}>
                  <HoneymoonSlider hotels={hotels ?? []} />
                </Suspense>
              </div>
              <div className='hidden md:block lg:block lgx2:block'>
                <LinkButton to='/honeymoon-holidays' btnDescription='View All Deals' />
              </div>
            </>
          )}
        </section>
        <hr className="mb-10 mt-10" />

        <section ref={sectionsRefs.beach} className="mx-4 my-4 sm:my-6 md:mx-0 md:my-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold hidden md:block lg:block lgx2:block">Relax and Unwind on Beach Holidays</h2>
              <h2 className="text-2xl font-semibold md:hidden lg:hidden lgx2:hidden">Beach Holidays</h2>
            </div>
          </div>
          <div className="flex flex-col">
            <ReadMoreLines mobileMaxRows={2} desktopMaxRows={2}>
              <p className="text-black md:mb-2 md:mt-2">
                We focus on creating unforgettable beach holidays, catering to couples, families, and solo travelers seeking sun-soaked escapes. Explore idyllic destinations such as the Maldives, Sri Lanka, Bali, the Caribbean, Thailand, and Mauritius, where pristine sands and crystal-clear waters await. Whether you desire a romantic getaway, a fun-filled family holiday, or a peaceful solo retreat, our carefully curated beach resorts promise relaxation and adventure. Experience the ultimate in luxury and comfort as you unwind by the ocean. Click below to discover our exclusive beach resorts and start planning your dream beach holiday today.
              </p>
            </ReadMoreLines>
          </div>
          {beachVisibility.hasBeenVisible && (
            <>
              <div className="hidden md:block lg:block lgx2:block mt-2">
                <Suspense fallback={<HotelCardSkeleton />}>
                  <BeachHotelsList hotels={hotels ?? []} />
                </Suspense>
              </div>
              <div className="container md:hidden lg:hidden">
                <Suspense fallback={<HotelSliderSkeleton />}>
                  <BeachSlider hotels={hotels ?? []} />
                </Suspense>
              </div>
              <div className='hidden md:block lg:block lgx2:block'>
                <LinkButton to='/beach-holidays' btnDescription='View All Deals' />
              </div>
            </>
          )}
        </section>
      </main>
    </>
  );
};

export default Home;
